import { Box, Text, useToast } from '@chakra-ui/react';
import { HotTable } from '@handsontable/react';
import * as _ from 'lodash';
import React, { useEffect } from 'react';

import { AssignmentReceivableGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  alwaysHiddenColumnFields,
  AssignmentEditTableKey,
  assignmentEditTableKeyToTitleMap,
  assignmentTablePadding,
  getBaseFieldColumnSettings,
  getHiddenColumnsFromLocalStorage,
  getInitialHiddenColumns,
  getRequiredAssignmentTableColumnHeaders,
  parseAssignmentReceivablesToTableComments,
  parseAssignmentReceivablesToTableData,
  unassignedReceivableOwnerLabel,
} from './utils';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.min.css';
import { colors } from 'packages/common-frontend/colors';
import {
  warnToastConfig,
  warnToastId,
} from 'packages/common-frontend/constants';

registerAllModules();

const checkboxColWidth = 50;
const standardColWidth = 300;
const selectAllCheckboxHeaderClass = 'selectAllCheckboxHeader';
let isHeaderChecked = false;

export const AssignmentEditHandsontable = ({
  tableKey,
  assignmentReceivables,
  creditFacilitiesForBorrowerMap,
  receivablesHotRef,
  moveColumnInAllTables,
  resizeColumnInAllTables,
  isAssigned,
}: {
  tableKey: AssignmentEditTableKey;
  assignmentReceivables: AssignmentReceivableGQLType[];
  creditFacilitiesForBorrowerMap: {
    [key: string]: { id: string; borrowerEntityName: string };
  };
  receivablesHotRef: any;
  moveColumnInAllTables: (
    tableKey: AssignmentEditTableKey,
    movedColumns: number[],
    dropIndex?: number,
  ) => void;
  resizeColumnInAllTables: (
    tableKey: AssignmentEditTableKey,
    newSize: number,
    column: number,
  ) => void;
  isAssigned: boolean;
}) => {
  const toast = useToast();

  const tableTitle = assignmentEditTableKeyToTitleMap[tableKey];

  let getDropdownOptionsSourceCallback: any;
  const rowsToHighlightForEligibility = new Set();
  const rowsToHighlightForConcentration = new Set();
  useEffect(() => {
    const receivablesHotRefCurrent = receivablesHotRef.current as any;
    const receivablesHot = receivablesHotRefCurrent?.hotInstance;

    /**
     * get proposed owner dropdown options per receivable row
     */

    // eslint-disable-next-line react-hooks/exhaustive-deps
    getDropdownOptionsSourceCallback = (row: number) => {
      const visualIndex = receivablesHot.rowIndexMapper.getVisualFromPhysicalIndex(
        row,
      );
      const assignableCreditFacilityIdsForReceivable = receivablesHot.getDataAtRowProp(
        visualIndex,
        'assignableCreditFacilityIds',
      );

      const dropdownOptions = _.map(
        assignableCreditFacilityIdsForReceivable,
        (cfid) => creditFacilitiesForBorrowerMap[cfid].borrowerEntityName,
      );
      return [...dropdownOptions, unassignedReceivableOwnerLabel];
    };

    /**
     * format and set comments for borrowing base kickouts to display at the borrowingBaseAmount column
     */
    const {
      eligibilityComments,
      concentrationComments,
    } = parseAssignmentReceivablesToTableComments(
      assignmentReceivables,
      receivablesHot?.propToCol('borrowingBaseAmount'),
    );
    const receivablesTableComments = [
      ...eligibilityComments,
      ...concentrationComments,
    ];

    receivablesHot?.updateSettings({
      cell: receivablesTableComments,
    });

    _.forEach(eligibilityComments, (comment) =>
      rowsToHighlightForEligibility.add(comment.row),
    );
    _.forEach(concentrationComments, (comment) =>
      rowsToHighlightForConcentration.add(comment.row),
    );

    receivablesHot?.render();
  });

  if (!assignmentReceivables.length) {
    return (
      <Box marginBottom="30px">
        <Text as="h2" fontSize="2xl" mb="15px" fontWeight="500">
          {tableTitle}
        </Text>
        <Text>There are no receivables currently in this category.</Text>
      </Box>
    );
  }

  const requiredColumnHeaders = getRequiredAssignmentTableColumnHeaders({
    isAssigned,
  });
  const baseFieldsColumnHeaders = _.map(
    assignmentReceivables[0].baseFields,
    ({ key }) => _.upperCase(key),
  );

  const alwaysHiddenColumnHeaders = _.map(
    alwaysHiddenColumnFields,
    (hiddenField) => {
      return _.capitalize(_.startCase(hiddenField));
    },
  );

  const allColumnHeaders = [
    ...requiredColumnHeaders,
    ...alwaysHiddenColumnHeaders,
    ...baseFieldsColumnHeaders,
  ];

  const { baseFieldsColumns, requiredColumns } = getBaseFieldColumnSettings(
    assignmentReceivables,
    isAssigned,
  );
  const alwaysHiddenColumns = _.map(
    alwaysHiddenColumnFields,
    (hiddenColumn) => {
      return {
        data: hiddenColumn,
      };
    },
  );

  const receivablesTableData = parseAssignmentReceivablesToTableData(
    assignmentReceivables,
    creditFacilitiesForBorrowerMap,
  );

  /**
   * MouseEvent listener that toggles checkbox state to select all or deselect all visual rows
   */
  const selectAllCheckboxes = () => {
    const receivablesHotRefCurrent = receivablesHotRef.current as any;
    const receivablesHot = receivablesHotRefCurrent?.hotInstance;

    const totalRows = receivablesHot.countRows();

    if (!isHeaderChecked) {
      isHeaderChecked = true;
      const updatesToChecked = _.map(_.range(0, totalRows), (rowIndex) => [
        rowIndex,
        'selected',
        true,
      ]);
      receivablesHot.setDataAtRowProp(updatesToChecked);
    } else {
      isHeaderChecked = false;
      const updatesToUnchecked = _.map(_.range(0, totalRows), (rowIndex) => [
        rowIndex,
        'selected',
        false,
      ]);
      receivablesHot.setDataAtRowProp(updatesToUnchecked);
    }

    receivablesHot.render();
  };

  return (
    <Box marginBottom="30px">
      <Text as="h2" fontSize="2xl" mb="15px" fontWeight="500">
        {tableTitle}
      </Text>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[300]}
        padding={assignmentTablePadding}
      >
        <HotTable
          id={tableKey}
          ref={receivablesHotRef}
          data={receivablesTableData}
          colHeaders={(colIndex) => {
            let checkboxHeader = '';
            switch (colIndex) {
              // for the first checkbox column, we attach a custom checkbox component to render here with a class attached
              // the custom class can then be targeted by event listeners and updated
              case 0:
                if (isAssigned) {
                  return checkboxHeader;
                }
                checkboxHeader = `<input type='checkbox' class='${selectAllCheckboxHeaderClass}' `;
                checkboxHeader += isHeaderChecked ? 'checked ' : '';
                checkboxHeader += '>';
                return checkboxHeader;
              default:
                return allColumnHeaders[colIndex];
            }
          }}
          columnHeaderHeight={50}
          manualColumnMove
          renderAllRows={false}
          dropdownMenu={[
            'filter_by_condition',
            'filter_by_value',
            'filter_action_bar',
          ]}
          filters
          readOnly
          className="assignment"
          height="400px"
          rowHeights={_.map(receivablesTableData, () => 50)}
          colWidths={_.concat(
            [checkboxColWidth], // first checkbox select column,
            Array(
              requiredColumnHeaders.length + baseFieldsColumnHeaders.length - 1,
            ).fill(standardColWidth),
          )}
          multiColumnSorting
          manualColumnResize
          afterColumnResize={(newSize, column) => {
            resizeColumnInAllTables(tableKey, newSize, column);
          }}
          outsideClickDeselects
          selectionMode="single"
          wordWrap
          columns={[
            ...requiredColumns,
            isAssigned
              ? {
                  data: 'owner',
                }
              : {
                  type: 'dropdown',
                  allowInvalid: false,
                  readOnly: false,
                  data: 'owner',
                  source(_query, callback) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    callback(getDropdownOptionsSourceCallback(this.row));
                  },
                },
            // always hidden columns are used only for internal mapping purposes and are permanently hidden (not included in dropdown to add columns)
            ...alwaysHiddenColumns,
            ...baseFieldsColumns,
          ]}
          hiddenColumns={
            getHiddenColumnsFromLocalStorage(tableKey) ||
            getInitialHiddenColumns({
              assignmentReceivable: assignmentReceivables[0],
              tableIsAssigned: isAssigned,
            })
          }
          fixedColumnsStart={1}
          cells={(row) => {
            if (rowsToHighlightForEligibility.has(row)) {
              return {
                className:
                  'assignment-cell assignment-cell-with-eligibility-kickout',
              };
            }
            if (rowsToHighlightForConcentration.has(row)) {
              return {
                className:
                  'assignment-cell assignment-cell-with-concentration-kickout',
              };
            }
            return {
              className: 'assignment-cell',
            };
          }}
          afterOnCellMouseDown={(event) => {
            if (
              _.includes(
                (event.target as Element).classList,
                selectAllCheckboxHeaderClass,
              )
            ) {
              selectAllCheckboxes();
            }
          }}
          comments
          afterColumnMove={(movedColumns, _finalIndex, dropIndex) => {
            // if the dropIndex does not exist, it means that the move was triggered by persistentStateLoad, instead of an actual column move
            // persistentStateLoad uses manualColumnMove under the hood, without a dropIndex
            if (dropIndex) {
              moveColumnInAllTables(tableKey, movedColumns, dropIndex);
            }
          }}
          beforeColumnMove={(movedColumns, finalIndex) => {
            const checkboxColIndex = receivablesHotRef?.current?.hotInstance.propToCol(
              'selected',
            );

            if (
              // if use attempts to move the checkbox column away from where it is, block attempt
              // the condition of being moved away from its original index is required otherwise this will toast every time a persistent state is being loaded
              // since persistent state column index load is technically a columnMove of all columns
              (_.includes(movedColumns, checkboxColIndex) &&
                _.isNumber(finalIndex) &&
                !_.isNaN(finalIndex) &&
                finalIndex !== checkboxColIndex) ||
              // if user attempts to move another column to where the checkbox column is, also block attempt
              (!_.includes(movedColumns, checkboxColIndex) &&
                finalIndex === checkboxColIndex)
            ) {
              if (!toast.isActive(warnToastId)) {
                warnToastConfig.title = `Please do not reorder the checkbox column (☑).`;
                toast(warnToastConfig);
              }
              return false;
            }
            return true;
          }}
          persistentState
          licenseKey="fd328-9396f-c8073-e983e-a1e13"
        />
      </Box>
    </Box>
  );
};
